import React from 'react';
import { getMaturityBand } from '../lib/utils';

/**
 * Sparkline — tiny inline trend chart (≤4 points).
 * Renders a delta chip (▲/▼/–) on the right.
 */
function Sparkline({ values = [], color = '#64748b', width = 56, height = 16 }) {
  const pts = (values || []).map((v) => Number(v?.score ?? v ?? 0));
  if (pts.length < 2) {
    return (
      <div
        className="text-[9px] text-slate-300 italic shrink-0"
        style={{ width, textAlign: 'right' }}
        title="Need at least 2 assessments for a trend"
      >
        —
      </div>
    );
  }
  const min = Math.min(...pts, 0);
  const max = Math.max(...pts, 100);
  const range = max - min || 1;
  const stepX = width / (pts.length - 1);
  const path = pts
    .map((v, i) => {
      const x = i * stepX;
      const y = height - ((v - min) / range) * (height - 2) - 1;
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
  const lastX = (pts.length - 1) * stepX;
  const lastY = height - ((pts[pts.length - 1] - min) / range) * (height - 2) - 1;
  const delta = pts[pts.length - 1] - pts[0];
  const deltaIcon = delta > 0.5 ? '▲' : delta < -0.5 ? '▼' : '–';
  const deltaColor = delta > 0.5 ? '#0d9488' : delta < -0.5 ? '#dc2626' : '#94a3b8';

  return (
    <div className="flex items-center gap-1 shrink-0" title={`Last ${pts.length} assessments: ${pts.map(p => Math.round(p)).join(' → ')}`}>
      <svg width={width} height={height} className="overflow-visible">
        <path d={path} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={lastX} cy={lastY} r={2} fill={color} />
      </svg>
      <span className="text-[9px] tabular-nums" style={{ color: deltaColor }}>
        {deltaIcon}{Math.abs(Math.round(delta))}
      </span>
    </div>
  );
}

/**
 * MaturityScoreCard — clean visual replacement for the radial gauge.
 *
 * Shows the overall maturity score + band as a header, then five
 * horizontal bars (one per pillar) so leaders can read relative strength
 * at a glance. Bars are colored by each pillar's own band, so weak
 * pillars (rose/amber) jump out next to strong ones (cyan/emerald).
 * If trend data is available (last 3-4 assessments), each pillar also
 * gets an inline sparkline + delta chip on the right.
 *
 * Props:
 *   - score: overall 0-100
 *   - pillarScores: [{ pillar_id, name, score, weight }]
 *   - pillarTrends: { pillar_id: [{score, created_at}, …] } — oldest first
 *   - overallTrend: [{score, created_at}, …] — oldest first
 *   - label: optional sub-label (e.g. "for ACME Corp")
 */
export default function MaturityGauge({
  score = 0, pillarScores = [], pillarTrends = {}, overallTrend = [], label,
}) {
  const s = Math.max(0, Math.min(100, Number(score) || 0));
  const overallBand = getMaturityBand(s);

  // Sort the pillar bars by score ascending so the primary constraint sits
  // at the top — same triage logic as the rest of the maturity surface.
  const bars = [...(pillarScores || [])].sort((a, b) => (a.score || 0) - (b.score || 0));

  return (
    <div data-testid="maturity-gauge" className="w-full">
      {/* Overall score header */}
      <div className="flex items-baseline gap-3 mb-1">
        <div className="text-5xl font-extrabold tabular-nums leading-none text-[#0F2040]">
          {Math.round(s)}
        </div>
        <div className="text-sm text-slate-400 tabular-nums">/ 100</div>
        {overallTrend && overallTrend.length >= 2 ? (
          <div data-testid="maturity-overall-trend">
            <Sparkline values={overallTrend} color={overallBand.color} width={64} height={18} />
          </div>
        ) : null}
        <div
          className="ml-auto px-3 py-1 rounded-full text-xs font-semibold tracking-wide"
          style={{ background: overallBand.color + '22', color: overallBand.color }}
          data-testid="maturity-gauge-band"
        >
          {overallBand.label}
        </div>
      </div>
      {label ? (
        <div className="text-xs text-slate-500 mb-3" data-testid="maturity-gauge-label">{label}</div>
      ) : null}

      {/* Threshold rail with tick marks */}
      <div className="relative h-1.5 mb-4 mt-2">
        <div className="absolute inset-0 rounded-full overflow-hidden flex">
          <div className="flex-1" style={{ background: '#fee2e2' }} />
          <div className="flex-1" style={{ background: '#fef3c7' }} />
          <div className="flex-1" style={{ background: '#cffafe' }} />
          <div className="flex-1" style={{ background: '#ccfbf1' }} />
        </div>
        {/* Today's overall score marker */}
        <div
          className="absolute -top-1 w-0.5 h-3.5 bg-[#0F2040]"
          style={{ left: `calc(${s}% - 1px)` }}
          aria-label={`Overall ${Math.round(s)}`}
        />
        <div className="absolute -bottom-4 left-0 right-0 flex justify-between text-[9px] text-slate-400">
          <span>0</span><span>25</span><span>50</span><span>75</span><span>100</span>
        </div>
      </div>

      {/* Pillar bars */}
      <div className="space-y-2 mt-6" data-testid="maturity-pillar-bars">
        {bars.length === 0 ? (
          <div className="text-xs text-slate-400 italic">No pillar data yet.</div>
        ) : bars.map((p) => {
          const pillarBand = getMaturityBand(p.score);
          const pct = Math.max(0, Math.min(100, Number(p.score) || 0));
          const trend = pillarTrends?.[p.pillar_id] || [];
          return (
            <div key={p.pillar_id} className="flex items-center gap-2" data-testid={`maturity-pillar-bar-${p.pillar_id}`}>
              <div className="w-32 shrink-0 text-[11px] font-medium text-slate-700 truncate" title={p.name}>
                {p.name}
              </div>
              <div className="flex-1 relative h-3 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 rounded-full transition-all duration-500"
                  style={{ width: `${pct}%`, background: pillarBand.color }}
                />
                {/* Subtle threshold lines at 25/50/75 */}
                <div className="absolute inset-y-0" style={{ left: '25%', width: '1px', background: 'rgba(255,255,255,0.6)' }} />
                <div className="absolute inset-y-0" style={{ left: '50%', width: '1px', background: 'rgba(255,255,255,0.6)' }} />
                <div className="absolute inset-y-0" style={{ left: '75%', width: '1px', background: 'rgba(255,255,255,0.6)' }} />
              </div>
              <div
                className="w-8 shrink-0 text-right text-xs font-bold tabular-nums"
                style={{ color: pillarBand.color }}
              >
                {Math.round(pct)}
              </div>
              <Sparkline values={trend} color={pillarBand.color} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

